import clsx from 'clsx';
import type { ToolCallState } from '@/hooks/use-streaming-v2';
import { formatNumber } from '@/lib/format';
import { PhaseShell, InfoRowsSkeleton } from './ToolResultRendererSkeleton';

interface DepNode {
  name: string;
  displayName: string;
  value: unknown;
  depth: number;
}

interface UpstreamNode extends DepNode {
  isInput: boolean;
}

interface DownstreamNode extends DepNode {
  isOutput: boolean;
}

interface DependencyData {
  name: string;
  displayName?: string;
  formula?: string;
  currentValue: unknown;
  upstream?: UpstreamNode[];
  downstream?: DownstreamNode[];
}

function isDependencyResult(data: unknown): data is DependencyData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'name' in data &&
    'currentValue' in data
  );
}

function formatValue(value: unknown): string {
  if (typeof value === 'number') return formatNumber(value, 4);
  if (value === null || value === undefined) return '-';
  return String(value);
}

function renderComplete(result: DependencyData) {
  const { name, displayName, formula, currentValue, upstream, downstream } = result;
  return (
    <div className="space-y-2.5">
      <div className="text-xs font-mono font-semibold text-accent tracking-[1.2px] uppercase">
        Dependencies: {displayName ?? name}
      </div>
      <div className="bg-bg border border-panel-border rounded-[2px] px-2.5 py-2">
        <div className="text-sm font-mono text-text-bright">{name}</div>
        {formula && (
          <div className="text-xs font-mono text-text-muted mt-0.5 break-all">{formula}</div>
        )}
        <div className="text-sm font-mono text-accent tabular-nums mt-1">{formatValue(currentValue)}</div>
      </div>
      {upstream && upstream.length > 0 && (
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px] mb-1">Upstream</div>
          <div className="space-y-0.5">
            {upstream.map((node, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-xs font-mono"
                style={{ paddingLeft: `${node.depth * 12}px` }}
              >
                <span className="flex items-center gap-1.5">
                  <span className={clsx(
                    'w-1 h-1 rounded-full',
                    node.isInput ? 'bg-blue' : 'bg-text-dim',
                  )} />
                  <span className={clsx(
                    node.isInput ? 'text-blue' : 'text-text-muted',
                  )}>
                    {node.displayName}
                  </span>
                </span>
                <span className="text-text tabular-nums">{formatValue(node.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {downstream && downstream.length > 0 && (
        <div>
          <div className="text-xs font-mono text-text-dim uppercase tracking-[1px] mb-1">Downstream</div>
          <div className="space-y-0.5">
            {downstream.map((node, i) => (
              <div
                key={i}
                className="flex items-center justify-between text-xs font-mono"
                style={{ paddingLeft: `${node.depth * 12}px` }}
              >
                <span className="flex items-center gap-1.5">
                  <span className={clsx(
                    'w-1 h-1 rounded-full',
                    node.isOutput ? 'bg-accent' : 'bg-text-dim',
                  )} />
                  <span className={clsx(
                    node.isOutput ? 'text-accent' : 'text-text-muted',
                  )}>
                    {node.displayName}
                  </span>
                </span>
                <span className="text-text tabular-nums">{formatValue(node.value)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface DependencyTreeProps {
  state: ToolCallState;
}

/**
 * Phase-aware DependencyTree. Per directive §7.7:
 * - skeleton: root node populated from `partialInput.name` as soon as available.
 * - in-progress: same root + "Resolving dependencies…" subtitle.
 * - complete: existing render.
 * - aborted: root + ABORTED badge.
 */
export function DependencyTree({ state }: DependencyTreeProps) {
  if (state.phase === 'complete' && isDependencyResult(state.result)) {
    return renderComplete(state.result);
  }
  if (state.phase === 'complete') {
    return (
      <div className="text-xs font-mono text-text-muted">
        <pre className="whitespace-pre-wrap">{JSON.stringify(state.result, null, 2)}</pre>
      </div>
    );
  }

  const inputData =
    state.phase === 'input_streaming' ? state.partialInput
    : state.phase === 'input_ready' || state.phase === 'executing' ? state.input
    : state.phase === 'aborted' ? (state.input ?? {})
    : {};
  const probe = inputData as Partial<{ name: string }>;
  const rootName = probe.name ?? '...';
  const variant = state.phase === 'aborted' ? 'aborted'
    : state.phase === 'executing' ? 'in-progress'
    : 'skeleton';

  return (
    <PhaseShell variant={variant} title={`Dependencies: ${rootName}`}>
      <div className="bg-bg border border-panel-border rounded-[2px] px-2.5 py-2">
        <div className="text-sm font-mono text-text-bright">{rootName}</div>
        {state.phase === 'executing' && (
          <div className="text-xs font-mono text-text-muted mt-0.5">Resolving dependencies…</div>
        )}
      </div>
      <InfoRowsSkeleton rows={2} />
    </PhaseShell>
  );
}
